import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Dialog, Transition } from "@headlessui/react";
import { ClipLoader } from "react-spinners";
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import axiosClient from "../../axiosClient";
import auth from "../../fire";
import PageHeader from "../../components/PageHeader/PageHeader";

// Only the subset of the user-metrics payload this screen needs. The list of
// users (and how much data each one owns) is reused from the existing
// GET /api/admin/user-metrics endpoint, so no extra "list users" endpoint is
// required.
interface DeletableUser {
  id: string;
  email: string | null;
  name: string | null;
  role: string;
  workouts: number;
  exercises: number;
  programs: number;
  workoutTemplates: number;
}

interface MetricsResponse {
  users: DeletableUser[];
}

interface DeleteSummary {
  workouts: number;
  exercises: number;
  workoutTemplates: number;
  programs: number;
  invites: number;
  emailChanges: number;
}

interface DeleteResponse {
  message: string;
  userId: string;
  email: string | null;
  firebaseAuthDeleted: boolean;
  firebaseAuthWarning: string | null;
  deleted: DeleteSummary;
}

interface Banner {
  user: DeletableUser;
  firebaseAuthDeleted: boolean;
  deleted: DeleteSummary;
}

// ---------------------------------------------------------------------------
// Confirmation modal
// ---------------------------------------------------------------------------
// The second, deliberately high-friction confirmation for an irreversible
// action. The "Delete permanently" button stays disabled until the admin types
// the target's exact email (GitHub-style). That typed value is also what gets
// sent to the server as `confirmEmail`, which re-verifies the match — so the
// same check is enforced on both the client and the server.
interface ConfirmModalProps {
  user: DeletableUser | null;
  isOpen: boolean;
  isDeleting: boolean;
  errorMessage: string | null;
  onCancel: () => void;
  onConfirm: (confirmEmail: string) => void;
}

const ConfirmDeleteUserModal = ({
  user,
  isOpen,
  isDeleting,
  errorMessage,
  onCancel,
  onConfirm,
}: ConfirmModalProps) => {
  // Untyped ref (matching the existing DeleteModal pattern) so it satisfies both
  // the <input> ref and headlessui's `initialFocus` without ref-variance friction.
  const inputRef = useRef(null);
  const [typed, setTyped] = useState("");

  // Reset the typed confirmation whenever the modal is opened for a (new) user.
  useEffect(() => {
    if (isOpen) setTyped("");
  }, [isOpen, user?.id]);

  // The exact string the admin must type to unlock deletion. Falls back to the
  // user id for the rare account with no email, mirroring the server.
  const confirmTarget = (user?.email || user?.id || "").trim();
  const canDelete =
    confirmTarget.length > 0 &&
    typed.trim().toLowerCase() === confirmTarget.toLowerCase();

  const handleConfirm = () => {
    if (!canDelete || isDeleting) return;
    onConfirm(typed.trim());
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={inputRef}
        // Don't let a stray backdrop click / Esc dismiss mid-delete.
        onClose={isDeleting ? () => {} : onCancel}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg mx-4 mb-24 w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <ExclamationTriangleIcon
                        className="h-6 w-6 text-red-600"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="mt-3 w-full sm:mt-0 sm:ml-4">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold leading-6 text-gray-900"
                      >
                        Permanently delete this user?
                      </Dialog.Title>

                      {user && (
                        <div className="mt-2 space-y-3">
                          <p className="text-sm text-gray-500">
                            This{" "}
                            <span className="font-semibold">
                              cannot be undone
                            </span>
                            . It permanently deletes{" "}
                            <span className="font-semibold text-gray-700">
                              {user.name || user.email || user.id}
                            </span>{" "}
                            and everything they own.
                          </p>

                          <div className="rounded-md bg-gray-50 border border-gray-200 p-3 text-sm">
                            <div className="truncate text-gray-700">
                              <span className="text-gray-500">Email: </span>
                              {user.email || "—"}
                            </div>
                            <div className="truncate text-xs text-gray-400 font-mono">
                              {user.id}
                            </div>
                            <div className="mt-2 grid grid-cols-4 gap-2 text-center">
                              {[
                                { label: "Workouts", value: user.workouts },
                                { label: "Exercises", value: user.exercises },
                                { label: "Programs", value: user.programs },
                                {
                                  label: "Templates",
                                  value: user.workoutTemplates,
                                },
                              ].map((stat) => (
                                <div
                                  key={stat.label}
                                  className="rounded-md bg-white border border-gray-200 py-1.5"
                                >
                                  <div className="text-base font-bold text-gray-900">
                                    {stat.value}
                                  </div>
                                  <div className="text-[10px] uppercase tracking-wide text-gray-500">
                                    {stat.label}
                                  </div>
                                </div>
                              ))}
                            </div>
                            <p className="mt-2 text-xs text-gray-400">
                              Their invites, pending email changes and Firebase
                              login are removed too.
                            </p>
                          </div>

                          <div>
                            <label
                              htmlFor="confirm-email"
                              className="block text-sm font-medium text-gray-700"
                            >
                              Type{" "}
                              <span className="font-mono font-semibold text-gray-900">
                                {confirmTarget}
                              </span>{" "}
                              to confirm
                            </label>
                            <input
                              id="confirm-email"
                              ref={inputRef}
                              type="text"
                              value={typed}
                              onChange={(e) => setTyped(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleConfirm();
                                }
                              }}
                              disabled={isDeleting}
                              spellCheck={false}
                              autoCapitalize="off"
                              autoCorrect="off"
                              autoComplete="off"
                              placeholder={confirmTarget}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 text-sm font-mono disabled:bg-gray-100"
                            />
                          </div>

                          {errorMessage && (
                            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 whitespace-pre-wrap">
                              {errorMessage}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  <button
                    type="button"
                    disabled={!canDelete || isDeleting}
                    onClick={handleConfirm}
                    className="inline-flex w-full justify-center items-center gap-2 rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {isDeleting ? (
                      <ClipLoader
                        color="white"
                        speedMultiplier={0.6}
                        size={"1.1rem"}
                      />
                    ) : (
                      <>
                        <TrashIcon className="h-4 w-4" />
                        Delete permanently
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    disabled={isDeleting}
                    onClick={onCancel}
                    className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none sm:mt-0 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------
const DeleteUser = () => {
  const [users, setUsers] = useState<DeletableUser[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const [selectedUser, setSelectedUser] = useState<DeletableUser | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [banner, setBanner] = useState<Banner | null>(null);

  // The signed-in admin — used to prevent selecting your own account for
  // deletion (the server blocks it too, but disabling it here is clearer).
  const currentUid = auth.currentUser?.uid;

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosClient.get<MetricsResponse>(
        "/api/admin/user-metrics",
      );
      setUsers(res.data.users);
    } catch (e) {
      const err = e as {
        response?: { status?: number; data?: { message?: string } };
      };
      if (err.response?.status === 403) {
        setError("You don't have admin access to view this page.");
      } else {
        setError(
          err.response?.data?.message ||
            "Failed to load users. Please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const visibleUsers = useMemo(() => {
    if (!users) return [];
    const query = search.trim().toLowerCase();
    const filtered = query
      ? users.filter(
          (u) =>
            (u.name || "").toLowerCase().includes(query) ||
            (u.email || "").toLowerCase().includes(query) ||
            u.id.toLowerCase().includes(query),
        )
      : users;
    return [...filtered].sort((a, b) =>
      (a.name || a.email || "").localeCompare(b.name || b.email || ""),
    );
  }, [users, search]);

  const handleRequestDelete = (user: DeletableUser) => {
    setSelectedUser(user);
    setDeleteError(null);
    setModalOpen(true);
  };

  const handleCancel = () => {
    if (isDeleting) return;
    setModalOpen(false);
    setSelectedUser(null);
    setDeleteError(null);
  };

  const handleConfirm = async (confirmEmail: string) => {
    // Capture into a local so the value stays non-null across the await and
    // inside the setUsers closure below.
    const target = selectedUser;
    if (!target) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const res = await axiosClient.delete<DeleteResponse>(
        `/api/admin/users/${target.id}`,
        { data: { confirmEmail } },
      );

      // Drop the deleted user from the list and surface a result banner.
      setUsers((prev) =>
        prev ? prev.filter((u) => u.id !== target.id) : prev,
      );
      setBanner({
        user: target,
        firebaseAuthDeleted: res.data.firebaseAuthDeleted,
        deleted: res.data.deleted,
      });
      setModalOpen(false);
      setSelectedUser(null);
    } catch (e) {
      const err = e as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      setDeleteError(
        err.response?.data?.message ||
          err.message ||
          "Failed to delete the user. Please try again.",
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <PageHeader backLink="/admin-dashboard" pageTitle="Delete User" />

      <div className="h-[calc(100%-63px)] overflow-y-auto bg-gray-50/50 p-4 space-y-4">
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          <span className="font-semibold">Danger zone.</span> Deleting a user
          permanently removes their account and all associated data (workouts,
          exercises, templates, programs, invites, email changes) and their
          Firebase login. This cannot be undone.
        </div>

        {/* Result of the last deletion */}
        {banner && (
          <div className="rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
            <div className="font-semibold">
              Deleted {banner.user.name || banner.user.email || banner.user.id}
            </div>
            <div className="mt-1 text-green-700">
              Removed {banner.deleted.workouts} workouts,{" "}
              {banner.deleted.exercises} exercises,{" "}
              {banner.deleted.workoutTemplates} templates,{" "}
              {banner.deleted.programs} programs, {banner.deleted.invites}{" "}
              invites and {banner.deleted.emailChanges} email changes.
            </div>
            {!banner.firebaseAuthDeleted && (
              <div className="mt-1 font-medium text-amber-700">
                Warning: the Firebase Auth account could not be removed
                automatically — delete it manually in the Firebase console.
              </div>
            )}
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-16">
            <ClipLoader color="#4f46e5" speedMultiplier={0.7} />
          </div>
        )}

        {!loading && error && (
          <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {error}
            <button
              type="button"
              onClick={loadUsers}
              className="ml-2 font-medium underline hover:text-red-900"
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && users && (
          <>
            {/* Search + refresh */}
            <div className="space-y-2">
              <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, email or id"
                  spellCheck={false}
                  autoCapitalize="off"
                  autoCorrect="off"
                  className="block w-full rounded-md border-gray-300 pl-9 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  Showing {visibleUsers.length} of {users.length} users
                </div>
                <button
                  type="button"
                  onClick={loadUsers}
                  aria-label="Refresh"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-500 shadow-sm hover:bg-gray-50 active:bg-gray-100"
                >
                  <ArrowPathIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* User list */}
            {visibleUsers.length === 0 ? (
              <div className="rounded-md border border-dashed border-gray-300 bg-white px-4 py-8 text-center text-sm text-gray-500">
                {users.length === 0
                  ? "No users yet."
                  : "No users match your search."}
              </div>
            ) : (
              <div className="space-y-2">
                {visibleUsers.map((u) => {
                  const isSelf = u.id === currentUid;
                  const isAdmin = u.role === "Admin";
                  // The server rejects deleting yourself or another admin; reflect
                  // that here by disabling the action with an explanatory label.
                  const blockedReason = isSelf
                    ? "You"
                    : isAdmin
                      ? "Admin"
                      : null;

                  return (
                    <div
                      key={u.id}
                      className="flex items-center gap-3 bg-white border border-gray-200 rounded-md p-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="min-w-0 truncate font-semibold text-gray-900">
                            {u.name || u.email || u.id}
                          </span>
                          {isAdmin && (
                            <span className="shrink-0 rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700">
                              Admin
                            </span>
                          )}
                        </div>
                        {u.email && (
                          <div className="mt-0.5 truncate text-xs text-gray-500">
                            {u.email}
                          </div>
                        )}
                        <div className="mt-0.5 text-[11px] text-gray-400">
                          {u.workouts} workouts · {u.exercises} exercises ·{" "}
                          {u.programs} programs · {u.workoutTemplates} templates
                        </div>
                      </div>

                      {blockedReason ? (
                        <span className="shrink-0 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-medium text-gray-400">
                          {blockedReason}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleRequestDelete(u)}
                          className="flex shrink-0 items-center gap-1 rounded-md border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-600 shadow-sm hover:bg-red-50 active:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                        >
                          <TrashIcon className="h-4 w-4" />
                          Delete
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmDeleteUserModal
        user={selectedUser}
        isOpen={modalOpen}
        isDeleting={isDeleting}
        errorMessage={deleteError}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
    </>
  );
};

export default DeleteUser;
